import { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import Locator from '../../../../../shared/Locator';
import { emptyConclusionData } from '../../../../../shared/types/licenses';
import { DependencySource } from '../../../../../shared/vendoredDependencies';
import { Action } from '../../../../../typings/permissions';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import { useIsFeatureEnabled } from '../../../../helpers/features/featureFlags';
import { useCurrentProject } from '../../../../hooks/api/projects/useCurrentProject';
import useProjectCorrection from '../../../../hooks/api/projects/useProjectCorrection';
import useNoticeFiles from '../../../../hooks/api/revisions/useNoticeFiles';
import useRevision, { getQueryKey as getRevisionQueryKey } from '../../../../hooks/api/revisions/useRevision';
import queryClient from '../../../../ReactQuery/client';
import * as DependenciesV2Store from '../../../../redux/dependenciesV2';
import useHasProjectPermission from '../../../../shared/permissions/useHasProjectPermission';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE } from '../../../generic/Button/button-constants';
import ButtonGroup from '../../../generic/Button/ButtonGroup';
import TooltipButton from '../../../generic/Button/TooltipButton';
import Flex from '../../../generic/Flex';
import useFromNow from '../../../generic/FromNow/useFromNow';
import { icons } from '../../../generic/Icon/icons';
import { Icon } from '../../../generic/Icon/index';
import Tabs from '../../../generic/Tabs';
import ModalEditDependency from '../../../modals/ModalEditDependency';
import ModalRemoveDependency from '../../../modals/ModalRemoveDependency';
import useModalRemoveDependency from '../../../modals/ModalRemoveDependency/useModalRemoveDependency';
import useDependencyMode from '../hooks/useDependencyMode';
import { SourceIcon } from '../SourceIcon';
import type { Dependency } from '../../../../../shared/dependenciesV2';

import DependencyLicenses from './content/DependencyLicenses/DependencyLicenses';
import DependencyMatchDetails from './content/DependencyMatchDetails';
import { DependencyAuthors } from './DependencyAuthors/DependencyAuthors';
import DependencyDetailsContainer from './DependencyDetails/DependencyDetailsContainer';
import { DependencyLabels } from './DependencyLabels/DependencyLabels';
import { DependencyNoticeContainer } from './DependencyNotices/DependencyNoticeContainer';
import { DependencyPaths } from './DependencyPaths/DependencyPaths';
import type { LicenseTabScope } from './content/DependencyLicenses/types';
import type { DrawerScope } from './types';

import styles from './DependencyDrawer.module.less';

const DeleteCorrectionConfirmationBody = () => (
  <Flex direction="column" gap={12} justify="flex-start" align="flex-start">
    <span>
      Are you sure you want to delete the corrections to this dependency across all projects and release groups?
    </span>
    <div>
      <span>This will remove all corrections to:</span>
      <ul>
        <li>Dependency metadata (title, description, URL, authors)</li>
        <li>NOTICE files</li>
        <li>Licenses</li>
        <li>License conclusions</li>
      </ul>
    </div>
  </Flex>
);

type DependencyDrawerTabs = 'details' | 'matches' | 'licenses' | 'paths' | 'labels' | 'authors' | 'notice';

type DependencyDrawerTitleProps = {
  dependency: Dependency;
  isVendoredDependenciesEnabled: boolean;
  isVendored: boolean;
};

const DependencyDrawerTitle: VoidFunctionComponent<DependencyDrawerTitleProps> = (
  { dependency, isVendoredDependenciesEnabled, isVendored },
) => {
  const label = isVendored ? 'Vendored ' : 'Managed ';
  // don't show a source prefix if vendored dependencies are not enabled
  const prefix = isVendoredDependenciesEnabled ? label : '';
  return (
    <div className={styles.titleHeader}>
      <div className={styles.sourceHeader}>{ prefix }Dependency</div>
      <span className={styles.sourceIcon}>
        {isVendored && <SourceIcon isVendored={isVendored} />}
        {!isVendored && <Icon icon={icons.boxArchive} />}
      </span>
      <span className={styles.title}>{dependency.title}</span>
      <span className={styles.version}>{dependency.version ?? Locator.parse(dependency.locator).revision}</span>
    </div>
  );
};

type Props = {
  dependency: Dependency;
  scope: DrawerScope;
  onEditDependency: () => void;
  onDeleteDependency: (locator: string) => void;
  onClose: () => void;
};

const DependencyContent: VoidFunctionComponent<Props> = ({
  dependency,
  scope,
  onEditDependency,
  onDeleteDependency,
  onClose,
}) => {
  const dialogContext = useDialogContext();
  const addToast = useAddToast();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<DependencyDrawerTabs>('details');
  const [isEditDependencyModalOpen, setIsEditDependencyModalOpen] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const { dependencyToRemove, setDependencyToRemove, removeDependency } = useModalRemoveDependency();
  const { data: currentProject } = useCurrentProject();

  // In order to determine if a dependency has been corrected, we need to check the revision + project for correction
  // fields, as well as the notice files.
  const { data: revision, isLoading: isRevisionLoading } = useRevision(dependency.locator, true);
  const {
    data: noticeFiles,
    isLoading: isNoticeFilesLoading,
    isError: isNoticeFilesError,
  } = useNoticeFiles(dependency.locator);

  const { deleteCorrection, rebuild } = useProjectCorrection(
    revision?.locator as string,
    scope.type === 'project' ? scope.revisionLocator : undefined,
  );

  const hasCorrection =
    (revision as { corrected?: boolean })?.corrected ||
    // @ts-expect-error projectCorrection really does exist on this response.
    !!revision?.project?.projectCorrection ||
    (noticeFiles && noticeFiles.length > 0 && noticeFiles.some((notice) => notice.corrected));

  const dependencyCacheUpdatedFromNow = useFromNow(revision?.dependency_cache_updated);
  const isBinaryDependency = Locator.parse(dependency.locator).fetcher === 'csbinary';

  const licenseTabScope: LicenseTabScope =
    scope.type === 'global'
      ? {
          dependencyRevisionLocator: dependency.locator,
          concludedLicenseCountMap: {},
          ...scope,
        }
      : {
          dependencyRevisionLocator: dependency.locator,
          ...scope,
        };

  const onDeleteCorrection = () => {
    dialogContext.onConfirm({
      title: 'Delete dependency correction',
      body: <DeleteCorrectionConfirmationBody />,
      onConfirm() {
        deleteCorrection.mutate(undefined, {
          onSuccess() {
            addToast({
              type: 'success',
              content: 'Dependency corrections have been deleted',
            });
            queryClient.invalidateQueries({
              // Wildcard invalidation for the dependency in both the project and release group scopes.
              predicate: (query) => {
                const key = query.queryKey;
                return key.includes('dependencies') && key.includes(dependency.locator);
              },
            });
            // Invalidate the revision query key to ensure the corrections are re-fetched.
            queryClient.invalidateQueries({
              queryKey: getRevisionQueryKey(dependency.locator),
            });
            dispatch(DependenciesV2Store.actions.preFetchLoading());
            dispatch(DependenciesV2Store.actions.fetchDependencies());
          },
          onError() {
            addToast({
              type: 'error',
              content: `Unable to delete dependency correction`,
            });
          },
        });
      },
    });
  };

  const isSuperUser = window.user?.super;
  const hasPermission = useHasProjectPermission(
    scope.type === 'project' ? scope.projectLocator : '',
    Action.Edit,
  );
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  const dependencyViewMode = useDependencyMode();

  // in a release group context, a dependency may be managed in some project and vendored in another
  // when showing all results in the release group managed table when vendored dependencies are enabled,
  // fall back to considering a dependency as vendored if it has any vendored paths at all in any project
  const doesDepHaveVendoredPaths = dependency.vendoredPaths.length !== 0;
  const doesViewShowMixedDeps = scope.type === 'releaseGroup' && dependencyViewMode === DependencySource.Managed;
  const mode = doesViewShowMixedDeps && doesDepHaveVendoredPaths ? DependencySource.Vendored : dependencyViewMode;
  const isVendored = isVendoredDependenciesEnabled && mode === DependencySource.Vendored;

  // isLicenseScanningEnabled determines the default state of a license without an issue.
  // When scanning is enabled, it is 'Approved'. When scanning is disabled, it is 'Unknown'.
  // We assume that scanning is enabled at the release group and global scopes.
  let isLicenseScanningEnabled = true;
  if (scope.type === 'project' && currentProject) {
    if (isVendored) {
      isLicenseScanningEnabled = currentProject.vendoredLicensingIssueScanningEnabled ?? isLicenseScanningEnabled;
    } else {
      isLicenseScanningEnabled = currentProject.licensingIssueScanningEnabled ?? isLicenseScanningEnabled;
    }
  }

  return (
    <>
      <div className={styles.dependencyContent}>
        <DependencyDrawerTitle
          dependency={dependency}
          isVendoredDependenciesEnabled={isVendoredDependenciesEnabled}
          isVendored={isVendored}
        />
        <div>
          <ButtonGroup>
            {scope.type === 'project' && (
              // Only show the delete/ignore button for project scope.
              <TooltipButton
                id="ignore-dependency-button-tooltip"
                showTooltip={!hasPermission}
                tooltip="You do not have permission to perform this action for this project."
                type="secondary"
                size={BUTTON_SIZE.MEDIUM}
                onClick={() => removeDependency(dependency.locator, dependency.isManual)}
                isDisabled={!hasPermission}
              >
                {dependency.isManual ? 'Delete' : 'Ignore'}
              </TooltipButton>
            )}
            <TooltipButton
              id="reanalyze-button-tooltip"
              // If the value is null, we want to hide the timestamp.
              showTooltip={!!revision?.dependency_cache_updated}
              // Show the exact timestamp to super users for debugging purposes, since it's not shown anywhere else in the
              // UI.
              tooltip={`Last analyzed: ${
                isSuperUser ? revision?.dependency_cache_updated : dependencyCacheUpdatedFromNow
              }`}
              type="secondary"
              size={BUTTON_SIZE.MEDIUM}
              isDisabled={isRebuilding || !hasPermission}
              onClick={() => {
                rebuild.mutate();
                setIsRebuilding(true);
                addToast({
                  type: 'success',
                  content: 'Dependency is being reanalyzed.',
                });
              }}
            >
              {isRebuilding ? <Icon icon={icons.checkOutline} /> : <Icon icon={icons.reload} />} Reanalyze
            </TooltipButton>
            {hasCorrection && (
              <Button type="error" size={BUTTON_SIZE.MEDIUM} onClick={onDeleteCorrection} isDisabled={isRevisionLoading}>
                Delete Correction
              </Button>
            )}
          </ButtonGroup>
        </div>
        <div className={styles.dependencyContentContainer}>
          <Tabs>
            <Tabs.Tab
              label="Details"
              key="details"
              isActive={activeTab === 'details'}
              onSelect={() => setActiveTab('details')}
            />
            {isBinaryDependency && (
              <Tabs.Tab
                label="Matches"
                key="matches"
                isActive={activeTab === 'matches'}
                onSelect={() => setActiveTab('matches')}
              />
            )}
            <Tabs.Tab
              label="Licenses"
              key="licenses"
              isActive={activeTab === 'licenses'}
              onSelect={() => setActiveTab('licenses')}
            />
            {scope.type === 'project' && (
              <Tabs.Tab
                label="Paths"
                key="paths"
                isActive={activeTab === 'paths'}
                onSelect={() => setActiveTab('paths')}
              />
            )}
            {scope.type === 'project' && (
              <Tabs.Tab
                label="Labels"
                key="labels"
                isActive={activeTab === 'labels'}
                onSelect={() => setActiveTab('labels')}
              />
            )}
            <Tabs.Tab
              label="Authors"
              key="authors"
              isActive={activeTab === 'authors'}
              onSelect={() => setActiveTab('authors')}
            />
            <Tabs.Tab
              label="NOTICE"
              key="notice"
              isActive={activeTab === 'notice'}
              onSelect={() => setActiveTab('notice')}
            />
          </Tabs>
          {activeTab === 'details' && <DependencyDetailsContainer dependency={dependency} />}
          {activeTab === 'matches' && isBinaryDependency && <DependencyMatchDetails dependency={dependency} />}
          {activeTab === 'licenses' && (
            <DependencyLicenses
              issues={dependency.issues}
              licenseGroups={dependency.licenseGroups}
              concludedLicenses={dependency.conclusions || emptyConclusionData}
              scope={licenseTabScope}
              isLoading={false}
              dependency={{
                title: dependency.title,
                version: dependency.version,
                locator: dependency.locator,
              }}
              shouldAllowEditing
              isLicenseScanningEnabled={isLicenseScanningEnabled}
            />
          )}
          {activeTab === 'paths' && scope.type === 'project' && (
            <DependencyPaths
              rootProjectLocator={scope.revisionLocator}
              dependencyLocator={dependency.locator}
              dependencyTitle={dependency.title}
              vendoredPaths={dependency.vendoredPaths}
            />
          )}
          {activeTab === 'labels' && scope.type === 'project' && (
            <DependencyLabels
              dependencyLocator={dependency.locator}
              dependencyTitle={dependency.title}
              parentRevisionLocator={scope.revisionLocator}
            />
          )}
          {activeTab === 'authors' && <DependencyAuthors dependency={dependency} />}
          {activeTab === 'notice' && (
            <DependencyNoticeContainer
              dependency={dependency}
              notices={noticeFiles}
              isLoading={isNoticeFilesLoading}
              isError={isNoticeFilesError}
            />
          )}
        </div>
      </div>
      <ModalEditDependency
        show={isEditDependencyModalOpen}
        onHide={() => setIsEditDependencyModalOpen(false)}
        onChange={() => {
          onEditDependency();
          setIsEditDependencyModalOpen(false);
        }}
        revisionLocator={dependency?.locator}
        parentRevisionLocator={dependency?.rootProjects.length === 1 ? dependency.rootProjects[0]?.revision : undefined}
      />
      {scope.type === 'project' && (
        <ModalRemoveDependency
          userRevisionLocator={scope.revisionLocator}
          dependencyToRemove={dependencyToRemove}
          setDependencyToRemove={setDependencyToRemove}
          onRemoveDependency={(locator: string) => {
            onDeleteDependency(locator);
            onClose();
          }}
        />
      )}
    </>
  );
};

export default DependencyContent;
